import { useEffect, useState } from 'react';
import { Loader2, MessageSquarePlus, Phone, Search, Headphones, ShoppingCart } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { Avatar } from '../../../components/Avatar';
import { useAuth } from '../../../lib/auth';
import { useToast } from '../../../components/Toast';
import { useAlertModal } from '../../../components/DialogProvider';
import { atendimentoApi, recusaComOutraAtendente } from '../lib/api';
import { api } from '../../../lib/api';
import { useListaTeclado } from '../../../lib/useListaTeclado';
import type { Contato, Conversa, Inbox } from '../lib/tipos';

interface Props {
  aberto: boolean;
  onFechar: () => void;
  onAberta?: (conversa: Conversa) => void;
  inboxInicial?: Inbox;
}

export function ModalNovaConversa({ aberto, onFechar, onAberta, inboxInicial }: Props) {
  const { usuario } = useAuth();
  const toast = useToast();
  const { alerta } = useAlertModal();

  function avisarFalha(e: any) {
    const outra = recusaComOutraAtendente(e);
    if (outra) alerta(outra, { titulo: 'Contato em atendimento', tipo: 'aviso' });
    else toast.erro(e?.message ?? 'Falha ao abrir');
  }

  const departamentos: Inbox[] = (usuario?.perfil === 'admin' || usuario?.perfil === 'gerente')
    ? ['atendimento', 'compras']
    : ((usuario?.departamentos as Inbox[] | undefined) ?? ['atendimento']) as Inbox[];

  const inboxPadrao = (inboxInicial && departamentos.includes(inboxInicial))
    ? inboxInicial
    : (departamentos[0] ?? 'atendimento');

  const [inbox, setInbox] = useState<Inbox>(inboxPadrao);
  const [telefone, setTelefone] = useState('');
  const [nome, setNome] = useState('');
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<Contato[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [abrindo, setAbrindo] = useState(false);
  const [checando, setChecando] = useState(false);
  const [check, setCheck] = useState<{ existe: boolean; incerto: boolean } | null>(null);
  const [porNumero, setPorNumero] = useState<Contato[]>([]);
  const teclado = useListaTeclado(resultados, (c) => abrirPorContato(c));

  useEffect(() => {
    if (!aberto) return;
    setTelefone('');
    setNome('');
    setBusca('');
    setResultados([]);
    setInbox(inboxPadrao);
    setCheck(null);
    setPorNumero([]);
  }, [aberto, inboxPadrao]);

  /**
   * Com o numero COMPLETO (10+ digitos), pergunta ao proprio WhatsApp se ele existe — a mesma peca
   * do "Enviar pelo WhatsApp" do orcamento (`checar-numero`). Nunca trava nada, so' informa
   * (decisao dele, 30/10/2026). Pela caixa escolhida no toggle: cada caixa e' uma conexao propria.
   */
  useEffect(() => {
    if (!aberto) return;
    const d = telefone.replace(/\D/g, '');
    if (d.length < 10) { setCheck(null); setChecando(false); return; }
    let cancelado = false;
    setChecando(true);
    const t = setTimeout(() => {
      api.get<{ existe: boolean; incerto?: boolean }>(
        `/api/contatos/checar-numero?telefone=${encodeURIComponent(d)}&inbox=${inbox}`,
      )
        .then((r) => { if (!cancelado) setCheck({ existe: !!r.existe, incerto: !!r.incerto }); })
        // Falha de rede nao acusa numero ruim: le como "nao deu pra conferir".
        .catch(() => { if (!cancelado) setCheck({ existe: true, incerto: true }); })
        .finally(() => { if (!cancelado) setChecando(false); });
    }, 400);
    return () => { cancelado = true; clearTimeout(t); };
  }, [telefone, inbox, aberto]);

  /**
   * Contatos que ja tem este numero, a partir de 4 DIGITOS (pedido dele, 30/10/2026: digitavam o
   * numero do LUIS como se fosse novo, e com "8742" queria ver todos que casam). Mesma busca do
   * campo de cima (`condicaoBuscaContato`): pedaco do numero so' pelos digitos, e com o numero
   * inteiro compara pela chave, que casa com e sem o 9o digito ([DA-737]).
   */
  useEffect(() => {
    if (!aberto) return;
    const d = telefone.replace(/\D/g, '');
    if (d.length < 4) { setPorNumero([]); return; }
    let cancelado = false;
    const t = setTimeout(() => {
      atendimentoApi.listarContatos({ busca: d, inbox, limit: 20, sem_grupo: true })
        .then((lst) => { if (!cancelado) setPorNumero(lst); })
        .catch(() => { if (!cancelado) setPorNumero([]); });
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [telefone, inbox, aberto]);

  // Busca debounced em contatos existentes
  useEffect(() => {
    if (!aberto) return;
    const q = busca.trim();
    if (q.length < 2) {
      setResultados([]);
      return;
    }
    let cancelado = false;
    setBuscando(true);
    const t = setTimeout(() => {
      atendimentoApi.listarContatos({ busca: q, inbox, limit: 20, sem_grupo: true })
        .then((lst) => { if (!cancelado) setResultados(lst); })
        .catch(() => { if (!cancelado) setResultados([]); })
        .finally(() => { if (!cancelado) setBuscando(false); });
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [busca, inbox, aberto]);

  async function abrirPorContato(c: Contato) {
    setAbrindo(true);
    try {
      const conv = await atendimentoApi.novaConversa({ jid: c.jid, inbox, nome: c.nome });
      toast.sucesso('Conversa aberta');
      onAberta?.(conv);
      onFechar();
    } catch (e: any) {
      avisarFalha(e);
    } finally {
      setAbrindo(false);
    }
  }

  async function abrirPorTelefone() {
    const tel = telefone.trim();
    if (!tel) return;
    const digits = tel.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.erro('Telefone muito curto');
      return;
    }
    setAbrindo(true);
    try {
      const conv = await atendimentoApi.novaConversa({ telefone: tel, inbox, nome: nome.trim() || undefined });
      toast.sucesso('Conversa aberta');
      onAberta?.(conv);
      onFechar();
    } catch (e: any) {
      avisarFalha(e);
    } finally {
      setAbrindo(false);
    }
  }

  return (
    <Modal aberto={aberto} onFechar={abrindo ? () => {} : onFechar} titulo="Nova conversa" largura={520}
      rodape={<button type="button" className="btn-secundario" onClick={onFechar} disabled={abrindo}>Cancelar</button>}>
      <div className="text-xs opacity-70 mb-3 flex items-start gap-2">
        <MessageSquarePlus size={14} className="mt-0.5 flex-shrink-0" />
        <span>Abre uma conversa nova com qualquer numero. Se ainda nao houver inbound, a primeira mensagem precisa ser texto livre (Cloud API permite janela de 24h apos a 1a inbound).</span>
      </div>

      {/* Inbox toggle */}
      {departamentos.length > 1 && (
        <div className="flex gap-1.5 mb-3 p-1 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.05)' }}>
          {departamentos.map((d) => {
            const ativo = inbox === d;
            const Icon = d === 'compras' ? ShoppingCart : Headphones;
            return (
              <button
                key={d}
                type="button"
                onClick={() => { setInbox(d); setCheck(null); }}
                className="flex-1 py-1.5 px-3 rounded text-sm font-medium transition flex items-center justify-center gap-1.5"
                style={{
                  backgroundColor: ativo ? 'var(--panel-bg, #fff)' : 'transparent',
                  color: ativo ? 'var(--page-fg, #000)' : 'inherit',
                  boxShadow: ativo ? '0 1px 2px rgba(0,0,0,0.06)' : undefined,
                }}
              >
                <Icon size={14} /> {d === 'compras' ? 'Compras' : 'Atendimento'}
              </button>
            );
          })}
        </div>
      )}

      {/* Busca de contatos existentes */}
      <div className="mb-3">
        <label className="text-[11px] uppercase tracking-wide opacity-60 block mb-1">Buscar contato existente</label>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={teclado.onKeyDown}
            placeholder="Nome, telefone..."
            disabled={abrindo}
            className="w-full pl-8 pr-3 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
            style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
          />
        </div>

        {buscando && (
          <div className="flex items-center justify-center py-3 opacity-70">
            <Loader2 size={14} className="animate-spin" />
          </div>
        )}

        {!buscando && resultados.length > 0 && (
          <div ref={teclado.listaRef} className="space-y-1 mt-2 max-h-40 overflow-y-auto pr-1">
            {resultados.map((c, idx) => {
              const titulo = [c.nome, c.sobrenome].filter(Boolean).join(' ') || c.push_name || c.telefone || c.jid;
              return (
                <button
                  key={c.id}
                  data-idx={idx}
                  type="button"
                  onClick={() => abrirPorContato(c)}
                  onMouseEnter={() => teclado.setHighlight(idx)}
                  disabled={abrindo}
                  className={`w-full flex items-center gap-2 p-2 rounded text-left disabled:opacity-50 transition ${idx === teclado.highlight ? 'bg-blue-50' : 'hover:bg-black/5'}`}
                  style={{ border: '1px solid var(--border, rgba(0,0,0,0.08))' }}
                >
                  <Avatar nome={titulo} fotoUrl={c.foto_url} tamanho={32} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{titulo}</div>
                    <div className="text-[11px] opacity-70 truncate">{c.telefone || c.jid}</div>
                  </div>
                  <span className="text-[11px] opacity-60 flex-shrink-0">Abrir</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Divisor */}
      <div className="flex items-center gap-3 my-3">
        <div className="flex-1 border-t" style={{ borderColor: 'var(--border, rgba(0,0,0,0.08))' }} />
        <span className="text-[11px] opacity-50 uppercase tracking-wide">ou</span>
        <div className="flex-1 border-t" style={{ borderColor: 'var(--border, rgba(0,0,0,0.08))' }} />
      </div>

      {/* Novo numero */}
      <div className="space-y-2">
        <label className="text-[11px] uppercase tracking-wide opacity-60 block">Numero novo</label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] opacity-70 block mb-0.5">Telefone *</label>
            <div className="relative">
              <Phone size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
              <input
                type="tel"
                value={telefone}
                onChange={(e) => { setTelefone(e.target.value); setCheck(null); }}
                placeholder="+55 64 9..."
                disabled={abrindo}
                className="w-full pl-7 pr-3 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
                style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] opacity-70 block mb-0.5">Nome (opcional)</label>
            {/* `no-upper`: nome de contato e' caixa livre (mesma regra do `PainelContato`), e sem
                esta classe o `text-transform` global mostraria maiusculo e gravaria o teclado. */}
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Como aparecera na lista"
              disabled={abrindo}
              className="no-upper w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
              style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
            />
          </div>
        </div>
        {/* ⚠️ O sinal so' acende DEPOIS da resposta chegar (`check && ...`), nunca por
            `!check?.existe` ([DA-649]); e `incerto` NAO vira recusa — copia do ModalEnviarWhatsApp. */}
        {check && (
          <div className="text-[11px] flex items-center gap-1.5" style={{
            color: check.incerto ? '#92400e' : check.existe ? '#166534' : '#991b1b',
          }}>
            {check.incerto
              ? '— nao deu pra conferir agora (WhatsApp desconectado); da pra abrir assim mesmo'
              : check.existe ? '✓ tem WhatsApp' : '× este numero nao tem WhatsApp'}
          </div>
        )}
        {checando && <div className="text-[11px] opacity-60">conferindo no WhatsApp...</div>}
        {porNumero.length > 0 && (
          <div>
            <div className="text-[11px] opacity-70 mb-1">
              {/* 20 e' o teto da consulta: com ele cheio, o total real pode ser maior. */}
              {porNumero.length >= 20
                ? '20 ou mais contatos tem estes digitos — digite mais para afunilar:'
                : porNumero.length === 1
                  ? 'Ja existe 1 contato com estes digitos:'
                  : `Ja existem ${porNumero.length} contatos com estes digitos:`}
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {porNumero.map((c) => {
                const titulo = [c.nome, c.sobrenome].filter(Boolean).join(' ') || c.push_name || c.telefone || c.jid;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => abrirPorContato(c)}
                    disabled={abrindo}
                    className="w-full flex items-center gap-2 p-2 rounded text-left disabled:opacity-50 transition hover:bg-black/5"
                    style={{ border: '1px solid var(--border, rgba(0,0,0,0.08))' }}
                  >
                    <Avatar nome={titulo} fotoUrl={c.foto_url} tamanho={32} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{titulo}</div>
                      <div className="text-[11px] opacity-70 truncate">{c.telefone || c.jid}</div>
                    </div>
                    <span className="text-[11px] opacity-60 flex-shrink-0">Abrir</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={abrirPorTelefone}
          disabled={abrindo || !telefone.trim()}
          className="w-full mt-1 py-2 rounded-lg btn-primario flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {abrindo ? <Loader2 size={14} className="animate-spin" /> : <MessageSquarePlus size={14} />}
          {abrindo ? 'Abrindo...' : 'Abrir conversa'}
        </button>
      </div>
    </Modal>
  );
}
